import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import { getGuests } from '@bridebook/web/lib/guestlist/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import { useWeddingWebsite } from '@bridebook/web/lib/wedding-website/hooks/query';
import { getWeddingWebsiteFlags } from '@bridebook/web/lib/wedding-website/hooks/query/selectors';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { isGuestWithEmail } from '../guests-helpers';

export enum CreateMessageFlowStep {
  Message = 'message',
  Recipients = 'recipients',
  Summary = 'summary',
}

export interface CreateMessageFlowData {
  message: {
    subject: string;
    message: string;
    includeGuestsNames: boolean;
    shareWeddingWebsiteLink: boolean;
    displayPasswordInEmail: boolean;
  };
  recipients: {
    guests: IGuest[];
  };
}

export interface CreateMessageFlowContextType {
  currentStep: CreateMessageFlowStep;
  formData: CreateMessageFlowData;
  setCurrentStep: (step: CreateMessageFlowStep) => void;
  updateFormData: (data: Partial<CreateMessageFlowData>) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  isLastStep: boolean;
  isFirstStep: boolean;
  forceCloseModal: () => void;
}

export const CreateMessageFlowContext = createContext<CreateMessageFlowContextType | undefined>(
  undefined,
);

interface IProps {
  children: ReactNode;
  forceCloseModal: () => void;
}

export const CreateMessageFlowProvider = ({ children, forceCloseModal }: IProps) => {
  const [currentStep, setCurrentStep] = useState<CreateMessageFlowStep>(
    CreateMessageFlowStep.Message,
  );

  const { data: weddingWebsiteFlags } = useWeddingWebsite({
    select: getWeddingWebsiteFlags,
  });

  const { isCreated, isPublished, hasPassword } = weddingWebsiteFlags ?? {};

  const guests = useSelector(getGuests);
  const guestsWithEmail = guests.filter(isGuestWithEmail);

  const initialFormData: CreateMessageFlowData = {
    message: {
      subject: '',
      message: '',
      includeGuestsNames: true,
      shareWeddingWebsiteLink: (isCreated && isPublished) ?? false,
      displayPasswordInEmail: (isCreated && isPublished && hasPassword) ?? false,
    },
    recipients: {
      guests: guestsWithEmail,
    },
  };

  const [formData, setFormData] = useState<CreateMessageFlowData>(initialFormData);

  const steps = useMemo(
    () => [
      CreateMessageFlowStep.Message,
      CreateMessageFlowStep.Recipients,
      CreateMessageFlowStep.Summary,
    ],
    [],
  );
  const currentStepIndex = steps.indexOf(currentStep);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1]);
    }
  }, [currentStepIndex, steps, setCurrentStep]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1]);
    }
  }, [currentStepIndex, steps, setCurrentStep]);

  const updateFormData = (data: Partial<CreateMessageFlowData>) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const value = useMemo(
    () => ({
      currentStep,
      formData,
      setCurrentStep,
      updateFormData,
      goToNextStep,
      goToPreviousStep,
      isLastStep: currentStepIndex === steps.length - 1,
      isFirstStep: currentStepIndex === 0,
      forceCloseModal,
    }),
    [
      currentStep,
      formData,
      goToNextStep,
      goToPreviousStep,
      currentStepIndex,
      steps.length,
      forceCloseModal,
    ],
  );

  return (
    <CreateMessageFlowContext.Provider value={value}>{children}</CreateMessageFlowContext.Provider>
  );
};

export const useCreateMessageFlow = () => {
  const context = useContext(CreateMessageFlowContext);
  if (context === undefined) {
    throw new Error('useCreateMessageFlow must be used within a CreateMessageFlowProvider');
  }
  return context;
};
